import { Button, ButtonGroup } from 'react-bootstrap';
import { DATETIME_FORMAT, DATE_FORMAT, TIME_FORMAT } from '@/constants';
import DateInputPopover, { DateInputPopoverProps } from '@/common/DateInput/DateInputPopover';
import { ForwardedRef, MouseEvent, ReactElement, forwardRef, useCallback, useEffect, useState } from 'react';
import TimeInputPopover, { TimeInputPopoverProps } from '@/common/TimeInput/TimeInputPopover';

import { Dayts } from '@/utils';
import useForwardRef from '@/hooks/useForwardRef';

export type DatetimeInputPopoverProps = DateInputPopoverProps & TimeInputPopoverProps;

const DatetimeInputPopover = forwardRef(
  (
    {
      value,
      format = DATETIME_FORMAT,
      showHours,
      showMinutes,
      showSeconds,
      disableHours,
      disableMinutes,
      disableSeconds,
      target,
      onChange,
      display,
    }: DatetimeInputPopoverProps,
    ref: ForwardedRef<HTMLDivElement>
  ): ReactElement => {
    const containerRef = useForwardRef<HTMLDivElement>(ref);
    const [maxHeight, setMaxHeight] = useState<number | undefined>(1);

    const getHeight = useCallback((): (() => void) => {
      if (!containerRef.current) {
        const timer = setTimeout(getHeight, 100);
        return (): void => clearTimeout(timer);
      }
      const height = (containerRef.current?.querySelector?.('.DateInput-Interface .Calendar-New') as HTMLDivElement)?.offsetHeight;
      if (height === maxHeight) return;
      setMaxHeight(height);
    }, [maxHeight, containerRef]);

    const handleChange =
      (type: 'date' | 'time') =>
      (newValue: string): string => {
        console.log('handleChange', type, newValue);
        let date = new Dayts(value || undefined);
        if (type === 'date') {
          date = date.setDate(newValue);
          console.log('handleChange date', date.format(format));
        } else {
          date = date.setTime(newValue);
          console.log('handleChange time', date.format(format));
        }
        const result = date.format(format);
        console.log('handleChange result', result);
        onChange(result);
        return result;
      };

    useEffect(() => {
      getHeight();
    }, [getHeight]);

    const Content = (
      <div className="DatetimeInput-Interface" ref={containerRef}>
        <DateInputPopover
          display
          value={value ? new Dayts(value).format(DATE_FORMAT) : undefined}
          format={DATE_FORMAT}
          target={target}
          onChange={handleChange('date')}
        />
        <TimeInputPopover
          display
          value={value ? new Dayts(value).format(TIME_FORMAT) : undefined}
          format={TIME_FORMAT}
          target={target}
          onChange={handleChange('time')}
          showHours={showHours}
          showMinutes={showMinutes}
          showSeconds={showSeconds}
          disableHours={disableHours}
          disableMinutes={disableMinutes}
          disableSeconds={disableSeconds}
          maxHeight={maxHeight}
        />
      </div>
    );

    if (display !== undefined && display !== false) return Content;
    return (
      <>
        {Content}
        <ButtonGroup className="DatetimeInput-Controls">
          <Button
            variant="outline-primary"
            onClick={(event: MouseEvent<HTMLButtonElement>): void => {
              const now = new Dayts().format(DATE_FORMAT);
              handleChange('date')(now);
              event.currentTarget.blur();
            }}
            tabIndex={-1}
          >
            Today
          </Button>
          <Button
            variant="outline-primary"
            onClick={(event: MouseEvent<HTMLButtonElement>): void => {
              const now = new Dayts().format(TIME_FORMAT);
              handleChange('time')(now);
              event.currentTarget.blur();
            }}
            tabIndex={-1}
          >
            Now
          </Button>
          <Button
            variant="outline-primary"
            onClick={(event: MouseEvent<HTMLButtonElement>): void => {
              target.current?.blur();
              event.currentTarget.blur();
            }}
            tabIndex={-1}
          >
            Ok
          </Button>
        </ButtonGroup>
      </>
    );
  }
);
DatetimeInputPopover.displayName = 'DatetimeInputPopover';

export default DatetimeInputPopover;
